import { Link, useLocation } from "react-router-dom";
import { User, Truck } from "lucide-react";
import { shouldUseMobileShell } from "@/mobile/MobileShellProvider";

/**
 * Top portal switcher — Customer vs Driver tabs with orange underline on active.
 * Admin is intentionally NOT exposed here (accessed via /admin URL directly).
 *
 * Hidden in mobile builds (Customer/Driver apps are shipped separately, so
 * cross-portal switching is meaningless on native).
 *
 * Usage:
 *   <PortalSwitcher />
 *
 * Active tab is inferred from the current pathname:
 *   - /driver/...  → Driver tab active
 *   - anything else → Customer tab active (the default)
 */
export default function PortalSwitcher() {
  const location = useLocation();
  if (shouldUseMobileShell()) return null;
  const isDriver = location.pathname.startsWith("/driver");

  const tabs = [
    { key: "customer", label: "For Customers",            short: "Customers", icon: User,  to: "/customer/login", active: !isDriver },
    { key: "driver",   label: "For Transport Providers",  short: "Providers", icon: Truck, to: "/driver/login",   active: isDriver },
  ];

  return (
    <div className="w-full bg-slate-100 border-b border-slate-200" data-testid="portal-switcher">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <nav className="flex items-center gap-6 sm:gap-10">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <Link
                key={t.key}
                to={t.to}
                className={`relative flex items-center gap-2 py-3 sm:py-4 text-sm sm:text-base font-semibold transition-colors ${
                  t.active ? "text-slate-900" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`portal-tab-${t.key}`}
              >
                <Icon className={`w-4 h-4 ${t.active ? "text-orange-500" : "text-slate-400"}`} />
                <span className="hidden sm:inline">{t.label}</span>
                <span className="sm:hidden">{t.short}</span>
                {t.active && (
                  <span className="absolute bottom-0 left-0 right-0 h-1 rounded-t-full bg-orange-500" aria-hidden />
                )}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
